<template>
  <div class="widget-body">
    <c-loader v-if="isLoading" />
    <v-alert show v-if="isNoData" variant="light" align="center">{{ $t('white-label.globalMessages.nodata') }}</v-alert>
    <div v-show="!isLoading && !isNoData" class="masonry">
      <div class="masonry-item master-data">
        <b-table
          dark
          small
          striped
          hover
          :fields="masterDataFields"
          :items="masterData"
          class="no-border-top"
          :aria-label="$t(`${i18nNamespace}.tables.masterData.title`)"
        >
          <template v-slot:head(title)>
            {{ $t(`${i18nNamespace}.tables.masterData.title`) }}
          </template>

          <template v-slot:cell(title)="data">{{
            $t(`${i18nNamespace}.tables.masterData.rowTitles.${data.item.title}`) +
              (data.item.title === 'marketCap' ? ' ' + currency : '')
          }}</template>
          <template v-slot:cell(html)="data">
            <v-fidlet
              v-if="data.item.title === 'price'"
              :data="data.value || ''"
              :type="masterMetadata['q._TRDPRC_1'].type"
            />
            <v-fidlet
              v-else-if="data.item.title === 'parValue'"
              :data="data.value || ''"
              :type="masterMetadata['rkd.SHARES_PAR_VALUE'].type"
            />
            <div v-else-if="data.item.title === 'marketCap'">
              <v-fidlet :data="parseFloat(data.value) / 1000 || ''" type="float" />
              {{ $t(`${i18nNamespace}.units.bn`) }}
            </div>
            <v-fidlet v-else-if="data.item.title === 'sharesOutstanding'" :data="data.value || ''" type="int" />
            <v-fidlet v-else-if="data.item.title === 'freeFloat'" :data="data.value || ''" type="float" />
            <v-fidlet v-else-if="data.item.title === 'employees'" :data="data.value || ''" type="int" />

            <span v-else v-html="data.value" />
          </template>
        </b-table>
      </div>
      <div class="masonry-item">
        <b-table
          dark
          small
          striped
          hover
          :fields="priceRelevantFields"
          :items="priceRelevantData"
          :aria-label="$t(`${i18nNamespace}.tables.priceRelevant.title`)"
        >
          <template v-slot:head(title)>
            {{ $t(`${i18nNamespace}.tables.priceRelevant.title`) }}
          </template>
          <template v-slot:cell(title)="data">{{
            $t(`${i18nNamespace}.tables.priceRelevant.rowTitles.${data.item.title}`)
          }}</template>
        </b-table>
      </div>
      <div class="masonry-item activities">
        <b-table
          dark
          small
          striped
          hover
          :fields="activitesFields"
          :items="activitiesData"
          class="no-border-top"
          :aria-label="$t(`${i18nNamespace}.tables.activites.title`)"
        >
          <template v-slot:head(value)>
            {{ $t(`${i18nNamespace}.tables.activites.title`) }}
          </template>
          <template v-slot:cell(value)="data">{{
            data.value !== 'noData' ? data.value : $t(`${i18nNamespace}.messages.${data.value}`)
          }}</template>
        </b-table>
      </div>
      <div class="masonry-item">
        <b-table
          dark
          small
          striped
          hover
          :fields="managementFields"
          :items="managementData"
          :aria-label="$t(`${i18nNamespace}.tables.management.title`)"
        >
          <template v-slot:head(title)>
            {{ $t(`${i18nNamespace}.tables.management.title`) }}
          </template>
        </b-table>
      </div>
    </div>

    <div class="text-center p-2" v-if="hasError && !isNoData">{{ $t(`${i18nNamespace}.messages.error`) }}</div>
  </div>
</template>

<script>
import CLoader from '@custom/CLoader';
import { API } from '@/state/api';

import moment from 'moment';

export default {
  name: 'w-company-profile-widget',
  components: {
    CLoader
  },
  props: {
    ric: {
      type: String,
      required: true
    }
  },
  watch: {
    ric: {
      handler() {
        this.fetch();
      }
    }
  },
  data() {
    return {
      isLoading: false,
      isNoData: false,
      hasError: false,
      currency: null,
      mandatoryMasterDataFids: [
        'q.RIC',
        'q._DSPLY_NAME',
        'rkd.COMP_ID',
        'q._TRDPRC_1',
        'rkdp.REPORT_CURR_PRIMARY_ISSUE',
        'q._EXCHG_NAME',
        'rkd.SHARES_PAR_VALUE',
        'rkd.SHARES_NUM_MOREY',
        'rkd.MKTCAP_MOREY',
        'rkd.TOTALFLOAT_PCT',
        'rkdp.EMPLOYEES_NUM_MOREY',
        'rkdp.COMP_ADDRESS_1',
        'rkdp.COMP_ADDRESS_4',
        'rkdp.COMP_ADDRESS_2',
        'rkdp.COMP_ADDRESS_5',
        'rkdp.COMP_PHONE',
        'rkdp.COMP_FAX_NO',
        'rkdp.COMP_WEB1',
        'rkd.SHARES_PAR_VALUE_CURR_CD',
        'rkdp.FINYEAREND_DATE',
        'rkdp.EXP_REPORTING_DATE_Q1',
        'rkdp.EXP_REPORTING_DATE_Q2',
        'rkdp.EXP_REPORTING_DATE_Q3',
        'rkdp.EXP_REPORTING_DATE_Q4',
        'rkdp.EXP_REPORTING_DATE_A'
      ],
      masterDataFields: [
        {
          key: 'title',
          class: 'w-50'
        },
        {
          key: 'html',
          label: '',
          class: 'w-50'
        }
      ],
      masterData: [],
      priceRelevantFields: [
        {
          key: 'title',
          class: 'w-50'
        },
        {
          key: 'value',
          label: '',
          class: 'w-50',
          formatter: (val, key, item) => {
            return item.title !== 'finYearEnd'
              ? this.$f(val, 'date')
              : this.getFiscalYearEndDate(this.$f(val, 'date.localed'));
          }
        }
      ],
      priceRelevantData: [],
      activitesFields: [
        {
          key: 'value',
          label: 'title'
        }
      ],
      activitiesData: [],
      managementFields: [
        {
          key: 'title',
          class: 'w-75'
        },
        {
          key: 'value',
          label: '',
          class: 'w-25'
        }
      ],
      managementData: [],
      i18nNamespace: 'white-label.widgets.companyProfile'
    };
  },
  methods: {
    fetch() {
      this.isLoading = true;
      this.isNoData = false;
      this.hasError = false;

      this.fetchMasterData()
        .then(compID => this.fetchManagementData(compID))
        .then(this.fetchISIN)
        .then(isin => this.fetchActivitiesData(isin))
        .catch(e => {
          console.log(e);
          this.isLoading = false;
          this.hasError = true;
        });
    },
    fetchMasterData() {
      return API.Rest.get(`/quote/info?rics=${this.ric}&fids=${this.mandatoryMasterDataFids}`).then(response => {
        if (response.data.data.length === 0) {
          this.isNoData = true;
          throw new Error('NO_MASTERDATA_RECEIVED');
        }

        const masterData = response.data.data;
        this.masterMetadata = response.data.metadata;

        this.masterData = this.processMasterData(masterData[0]);
        this.priceRelevantData = this.processPriceRelevantData(masterData[0]);

        return masterData[0]['rkd.COMP_ID'];
      });
    },
    fetchManagementData(comp_id) {
      return API.Rest.get(`/company/officers/?repno=${comp_id}`).then(response => {
        if (response.data.data.length === 0) {
          throw new Error('NO_MANAGEMENT_DATA_RECEIVED');
        }

        this.managementData = this.processManagementData(response.data.data);
      });
    },
    fetchISIN() {
      return API.Rest.get(`/quote/info?rics=${this.ric}&fids=code.ISIN`).then(response => {
        const data = response.data.data;
        return data[0]['code.ISIN'];
      });
    },
    fetchActivitiesData(isin) {
      return API.Rest.get(`/company/description?isins=${isin}&language=EN`).then(response => {
        this.activitiesData = this.processActivitesData(response.data.data[0]);

        this.isLoading = false;
      });
    },
    processMasterData(masterData) {
      this.currency = masterData['rkdp.REPORT_CURR_PRIMARY_ISSUE']; // needed for masterData formmatter function

      let result = [
        {
          title: 'symbol',
          html: masterData['q.RIC']
        },
        {
          title: 'name',
          html: masterData['q._DSPLY_NAME']
        },
        {
          title: 'price',
          html: masterData['q._TRDPRC_1']
        },
        {
          title: 'currency',
          html: masterData['rkdp.REPORT_CURR_PRIMARY_ISSUE']
        },
        {
          title: 'exchange',
          html: masterData['q._EXCHG_NAME']
        },
        {
          title: 'parValue',
          html: masterData['rkd.SHARES_PAR_VALUE']
        },
        {
          title: 'sharesOutstanding',
          html: masterData['rkd.SHARES_NUM_MOREY']
        },
        {
          title: 'marketCap',
          html: masterData['rkd.MKTCAP_MOREY']
        },
        {
          title: 'freeFloat',
          html: masterData['rkd.TOTALFLOAT_PCT']
        },
        {
          title: 'employees',
          html: masterData['rkdp.EMPLOYEES_NUM_MOREY']
        },
        {
          title: 'headquarters',
          html:
            masterData['rkdp.COMP_ADDRESS_1'] +
            ', <br />' +
            masterData['rkdp.COMP_ADDRESS_4'] +
            ',<br />' +
            masterData['rkdp.COMP_ADDRESS_2'] +
            ', ' +
            masterData['rkdp.COMP_ADDRESS_5']
        },
        {
          title: 'phone',
          html: '+' + masterData['rkdp.COMP_PHONE']
        },
        {
          title: 'fax',
          html:
            masterData['rkdp.COMP_FAX_NO'] !== '-'
              ? '+' + masterData['rkdp.COMP_FAX_NO']
              : masterData['rkdp.COMP_FAX_NO']
        },
        {
          title: 'internet',
          html:
            '<a href="' + masterData['rkdp.COMP_WEB1'] + '" target="_blank">' + masterData['rkdp.COMP_WEB1'] + '</a>'
        }
      ];

      return result;
    },
    getFiscalYearEndDate(fiscalYearEndDate) {
      let separator = null;

      if (fiscalYearEndDate.indexOf('/') !== -1) {
        separator = {
          format: 'MM/DD/YYYY',
          output: 'MM/DD'
        };
      } else if (fiscalYearEndDate.indexOf('-') !== -1) {
        separator = {
          format: 'YYYY-MM-DD',
          output: 'MM-DD'
        };
      } else {
        separator = {
          format: 'DD.MM.YYYY',
          output: 'DD.MM'
        };
      }

      return moment(fiscalYearEndDate, separator.format).format(separator.output);
    },
    processPriceRelevantData(masterData) {
      let result = [
        {
          title: 'finYearEnd',
          value: masterData['rkdp.FINYEAREND_DATE']
        },
        {
          title: 'reportingDateQ1',
          value: masterData['rkdp.EXP_REPORTING_DATE_Q1']
        },
        {
          title: 'reportingDateQ2',
          value: masterData['rkdp.EXP_REPORTING_DATE_Q2']
        },
        {
          title: 'reportingDateQ3',
          value: masterData['rkdp.EXP_REPORTING_DATE_Q3']
        },
        {
          title: 'reportingDateQ4',
          value: masterData['rkdp.EXP_REPORTING_DATE_Q4']
        },
        {
          title: 'reportingDateA',
          value: masterData['rkdp.EXP_REPORTING_DATE_A']
        }
      ];

      return result;
    },
    processManagementData(managementData) {
      let result = [];
      let title = '',
        fullname = '';

      managementData.forEach(function(person) {
        title = person.PROL_TITLE;
        fullname = person.PERS_NAME_PREFIX ? person.PERS_NAME_PREFIX + ' ' : '';
        fullname += person.PERS_NAME_FIRST ? person.PERS_NAME_FIRST + ' ' : '';
        fullname += person.PERS_NAME_MIDDLE && person.PERS_NAME_MIDDLE !== '-' ? person.PERS_NAME_MIDDLE + ' ' : '';
        fullname += person.PERS_NAME_LAST ? person.PERS_NAME_LAST + ' ' : '';
        fullname += person.PERS_NAME_SUFFIX && person.PERS_NAME_SUFFIX !== '-' ? person.PERS_NAME_SUFFIX + ' ' : '';

        result.push({
          title: title,
          value: fullname
        });
      });

      return result;
    },
    processActivitesData(activitiesData) {
      return [
        {
          value: activitiesData !== undefined ? activitiesData['COMPANY_BUSINESS_SUMMARY'] : 'noData'
        }
      ];
    }
  },
  mounted() {
    this.fetch();
  }
};
</script>
